import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export type EntityType = 'property' | 'contract' | 'tenant' | 'repair' | 'maintenance' | 'building' | 'loan' | 'service-provider' | 'investment-calculator';
export type ParentEntityType = 'property' | 'building' | 'contract' | 'tenant' | 'loan' | 'investment-calculator';

export interface NavigationContext {
  // Source information
  sourceType: 'list' | 'detail' | 'kanban' | 'dashboard' | 'search' | 'add' | 'edit' | 'unknown';
  sourceRoute: string;
  sourceParams?: Record<string, unknown>;
  sourceQueryParams?: Record<string, unknown>;

  // Entity context
  entityType?: EntityType;
  entityId?: string;
  entityName?: string;

  // Parent context (for nested entities)
  parentEntityType?: ParentEntityType;
  parentEntityId?: string;
  parentEntityName?: string;
  parentTabOrSection?: string;

  // Navigation metadata
  timestamp: Date;
  breadcrumbs?: string[];
}

export interface ReturnNavigationOptions {
  preserveQueryParams?: boolean;
  additionalParams?: Record<string, unknown>;
  fallbackRoute?: string;
  showSuccessMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContextAwareNavigationService {
  private _currentContext$ = new BehaviorSubject<NavigationContext | null>(null);
  private _navigationHistory: NavigationContext[] = [];

  private readonly MAX_HISTORY_SIZE = 10;
  private _isNavigatingBack = false;

  // Public observables
  currentContext$ = this._currentContext$.asObservable();

  private readonly destroyRef = inject(DestroyRef);

  constructor(private router: Router) {
    this.initializeRouterTracking();
  }

  /**
   * Initialize router event tracking to automatically capture navigation context
   */
  private initializeRouterTracking(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event => event as NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(event => {
      if (this._isNavigatingBack) {
        this._isNavigatingBack = false;
        this.handleBackNavigation(event.urlAfterRedirects);
      } else {
        this.captureNavigationContext(event.urlAfterRedirects);
      }
    });
  }

  private captureNavigationContext(url: string): void {
    const context = this.analyzeRoute(url);

    // Add to history
    this._navigationHistory.unshift(context);
    if (this._navigationHistory.length > this.MAX_HISTORY_SIZE) {
      this._navigationHistory = this._navigationHistory.slice(0, this.MAX_HISTORY_SIZE);
    }

    // Update current context
    this._currentContext$.next(context);
  }

  /**
   * Handle context update during back navigation
   * Pops previous context and validates if new context needs to be added
   */
  private handleBackNavigation(url: string): void {
    const context = this.analyzeRoute(url);

    // Remove the context we are leaving (current top)
    if (this._navigationHistory.length > 0) {
      this._navigationHistory.shift();
    }

    // Check if the destination (new top) matches where we landed
    const newTop = this._navigationHistory[0];
    const isReturningToHistory = newTop &&
      (newTop.sourceRoute === context.sourceRoute ||
        (newTop.entityType === context.entityType && newTop.entityId === context.entityId));

    // If we didn't land exactly where history expected (e.g. fallback), 
    // or if history was empty, add this new state.
    if (!isReturningToHistory) {
      this._navigationHistory.unshift(context);
    }

    // Always update current context
    this._currentContext$.next(context);
  }

  /**
   * Analyze route URL to determine context
   */
  private analyzeRoute(url: string): NavigationContext {
    const segments = url.split('?')[0].split('/').filter(s => s);
    const queryParams = this.parseQueryParams(url);

    const context: NavigationContext = {
      sourceRoute: url,
      sourceQueryParams: queryParams,
      timestamp: new Date(),
      sourceType: 'unknown'
    };

    // Analyze route patterns
    if (segments.includes('properties')) {
      this.analyzePropertyRoute(segments, context);
    } else if (segments.includes('repairs')) {
      this.analyzeRepairRoute(segments, context);
    } else if (segments.includes('maintenance')) {
      this.analyzeMaintenanceRoute(segments, context);
    } else if (segments.includes('contracts')) {
      this.analyzeContractRoute(segments, context);
    } else if (segments.includes('tenants')) {
      this.analyzeTenantRoute(segments, context);
    } else if (segments.includes('loans')) {
      this.analyzeLoanRoute(segments, context);
    } else if (segments.includes('property-evaluation')) {
      this.analyzePropertyEvaluationRoute(segments, context);
    } else if (segments.includes('service-providers')) {
      this.analyzeServiceProviderRoute(segments, context);
    } else if (segments.includes('task-management-kanban')) {
      context.sourceType = 'kanban';
      context.entityType = 'repair'; // Kanban typically shows repairs/maintenance
    }

    return context;
  }

  /**
   * Analyze property-related routes
   */
  private analyzePropertyRoute(segments: string[], context: NavigationContext): void {
    const propertyIndex = segments.indexOf('properties');

    if (segments[propertyIndex + 1] === 'list') {
      context.sourceType = 'list';
      context.entityType = 'property';
    } else if (segments[propertyIndex + 1] && segments[propertyIndex + 1] !== 'add') {
      // Property detail route
      context.sourceType = 'detail';
      context.entityType = 'property';
      context.entityId = segments[propertyIndex + 1];

      // Check for sub-sections
      if (segments[propertyIndex + 2]) {
        const section = segments[propertyIndex + 2];

        // Handle nested sections like details/characteristics, building/features
        if (segments[propertyIndex + 3] &&
            !['create', 'detail', 'edit'].includes(segments[propertyIndex + 3])) {
          // Combine parent and child section (e.g., "details/characteristics")
          context.parentTabOrSection = `${section}/${segments[propertyIndex + 3]}`;
        } else {
          context.parentTabOrSection = section;
        }

        // Check for nested operations (create repair/maintenance from property)
        if (segments[propertyIndex + 3] === 'create') {
          context.sourceType = 'detail';
          if (section === 'repairs' || section === 'maintenance') {
            context.parentEntityType = 'property';
            context.parentEntityId = context.entityId;
            // Get property name from query params if available
            if (context.sourceQueryParams?.['propertyName']) {
              context.parentEntityName = String(context.sourceQueryParams['propertyName']);
            }
          }
        }
      }
    }
  }

  /**
   * Analyze repair-related routes
   */
  private analyzeRepairRoute(segments: string[], context: NavigationContext): void {
    const repairIndex = segments.indexOf('repairs');

    if (segments[repairIndex + 1] === 'list') {
      context.sourceType = 'list';
      context.entityType = 'repair';
    } else if (segments[repairIndex + 1] === 'detail') {
      context.sourceType = 'detail';
      context.entityType = 'repair';
      context.entityId = segments[repairIndex + 2];
    }
  }

  /**
   * Analyze maintenance-related routes
   */
  private analyzeMaintenanceRoute(segments: string[], context: NavigationContext): void {
    const maintenanceIndex = segments.indexOf('maintenance');

    if (segments[maintenanceIndex + 1] === 'list') {
      context.sourceType = 'list';
      context.entityType = 'maintenance';
    } else if (segments[maintenanceIndex + 1] === 'detail') {
      context.sourceType = 'detail';
      context.entityType = 'maintenance';
      context.entityId = segments[maintenanceIndex + 2];
    }
  }

  /**
   * Analyze contract-related routes
   */
  private analyzeContractRoute(segments: string[], context: NavigationContext): void {
    const contractIndex = segments.indexOf('contracts');

    if (segments[contractIndex + 1] === 'list') {
      context.sourceType = 'list';
      context.entityType = 'contract';
    } else if (segments[contractIndex + 1] === 'detail') {
      context.sourceType = 'detail';
      context.entityType = 'contract';
      context.entityId = segments[contractIndex + 2];
    }
  }

  /**
   * Analyze tenant-related routes
   */
  private analyzeTenantRoute(segments: string[], context: NavigationContext): void {
    const tenantIndex = segments.indexOf('tenants');

    if (segments[tenantIndex + 1] === 'list') {
      context.sourceType = 'list';
      context.entityType = 'tenant';
    } else if (segments[tenantIndex + 1] && segments[tenantIndex + 1] !== 'add') {
      context.sourceType = 'detail';
      context.entityType = 'tenant';
      context.entityId = segments[tenantIndex + 1];

      if (segments[tenantIndex + 2]) {
        context.parentTabOrSection = segments[tenantIndex + 2];
      }
    } else if (segments.length === 1 && segments[0] === 'tenants') {
      // Handle /tenants route (which should redirect to /tenants/list)
      context.sourceType = 'list';
      context.entityType = 'tenant';
    }
  }

  /**
   * Analyze loan-related routes
   */
  private analyzeLoanRoute(segments: string[], context: NavigationContext): void {
    const loanIndex = segments.indexOf('loans');

    if (segments[loanIndex + 1] === 'list') {
      context.sourceType = 'list';
      context.entityType = 'loan';
    } else if (segments[loanIndex + 1] === 'detail') {
      context.sourceType = 'detail';
      context.entityType = 'loan';
      context.entityId = segments[loanIndex + 2];
    } else if (segments[loanIndex + 1] === 'add') {
      context.sourceType = 'add';
      context.entityType = 'loan';
    } else if (segments[loanIndex + 1] === 'edit') {
      context.sourceType = 'edit';
      context.entityType = 'loan';
      context.entityId = segments[loanIndex + 2];
    } else if (segments.length === 1 && segments[0] === 'loans') {
      // Handle /loans route (which should redirect to /loans/list)
      context.sourceType = 'list';
      context.entityType = 'loan';
    }
  }

  /**
   * Analyze property evaluation routes (investment calculator)
   */
  private analyzePropertyEvaluationRoute(segments: string[], context: NavigationContext): void {
    const evaluationIndex = segments.indexOf('property-evaluation');

    if (segments[evaluationIndex + 1] === 'investment-calculator') {
      const calcIndex = evaluationIndex + 1;

      if (segments[calcIndex + 1] === 'list') {
        context.sourceType = 'list';
        context.entityType = 'investment-calculator';
      } else if (segments[calcIndex + 1] && segments[calcIndex + 1] !== 'new') {
        context.sourceType = 'detail';
        context.entityType = 'investment-calculator';
        context.entityId = segments[calcIndex + 1];

        if (segments[calcIndex + 2]) {
          context.parentTabOrSection = segments[calcIndex + 2];
        }
      }
    }
  }

  /**
   * Analyze service provider-related routes
   */
  private analyzeServiceProviderRoute(segments: string[], context: NavigationContext): void {
    const providerIndex = segments.indexOf('service-providers');

    if (segments[providerIndex + 1] === 'public') {
      context.sourceType = 'list';
      context.entityType = 'service-provider';
      context.parentTabOrSection = 'public';
    } else if (segments[providerIndex + 1] === 'add') {
      context.sourceType = 'add';
      context.entityType = 'service-provider';
    } else if (segments[providerIndex + 1] === 'edit') {
      context.sourceType = 'edit';
      context.entityType = 'service-provider';
      context.entityId = segments[providerIndex + 2];
    } else if (segments[providerIndex + 1] && segments[providerIndex + 1] !== '') {
      context.sourceType = 'detail';
      context.entityType = 'service-provider';
      context.entityId = segments[providerIndex + 1];
    } else {
      context.sourceType = 'list';
      context.entityType = 'service-provider';
    }
  }

  /**
   * Parse query parameters from URL
   */
  private parseQueryParams(url: string): Record<string, string> {
    const queryString = url.split('?')[1];
    if (!queryString) return {};

    const params: Record<string, string> = {};
    queryString.split('&').forEach(param => {
      const [key, value] = param.split('=');
      if (key && value) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });

    return params;
  }

  /**
   * Set explicit navigation context (for manual context setting)
   */
  setNavigationContext(context: Partial<NavigationContext>): void {
    const fullContext: NavigationContext = {
      sourceType: 'unknown',
      sourceRoute: this.router.url,
      timestamp: new Date(),
      ...context
    };

    this._currentContext$.next(fullContext);
    this._navigationHistory.unshift(fullContext);
  }

  /**
   * Set cross-entity navigation context (when navigating from one entity to another)
   * This preserves the original context so back navigation works correctly
   */
  setCrossEntityContext(targetEntityType: string, targetEntityId: string, originalContext?: NavigationContext): void {
    const currentContext = originalContext || this._currentContext$.value;

    const crossEntityContext: NavigationContext = {
      sourceType: 'detail',
      sourceRoute: this.router.url,
      entityType: targetEntityType as EntityType,
      entityId: targetEntityId,
      timestamp: new Date(),
      // Preserve the original context as parent context
      parentEntityType: currentContext?.entityType as ParentEntityType | undefined,
      parentEntityId: currentContext?.entityId,
      parentTabOrSection: currentContext?.parentTabOrSection
    };

    this._currentContext$.next(crossEntityContext);
    this._navigationHistory.unshift(crossEntityContext);
  }

  /**
   * Get the appropriate return route based on current context
   */
  getReturnRoute(options: ReturnNavigationOptions = {}): string {
    const context = this._currentContext$.value;

    if (!context) {
      return options.fallbackRoute || '/';
    }

    // PRIORITY 1: Handle cross-entity navigation (return to parent entity)
    if (context.parentEntityType && context.parentEntityId) {
      const section = context.parentTabOrSection || 'overview';

      switch (context.parentEntityType) {
        case 'property':
          return `/properties/${context.parentEntityId}/${section}`;
        case 'contract':
          return `/contracts/detail/${context.parentEntityId}/${section}`;
        case 'tenant':
          return `/tenants/${context.parentEntityId}/${section}`;
        case 'building':
          return `/building/${context.parentEntityId}/${section}`;
        case 'loan':
          return `/loans/detail/${context.parentEntityId}/${section}`;
        case 'investment-calculator':
          return `/property-evaluation/investment-calculator/${context.parentEntityId}/${section}`;
        default:
          return this.getDetailRoute(context.parentEntityType, context.parentEntityId);
      }
    }

    // PRIORITY 2: Look for the most recent list view in navigation history for the same entity type
    const listContext = this._navigationHistory.find(ctx =>
      ctx.sourceType === 'list' && ctx.entityType === context.entityType
    );

    if (listContext) {
      return listContext.sourceRoute;
    }

    // PRIORITY 3: Look for any recent context that isn't the current entity type (cross-entity return)
    const crossEntityContext = this._navigationHistory.find(ctx =>
      ctx.entityType !== context.entityType &&
      (ctx.sourceType === 'detail' || ctx.sourceType === 'list') &&
      ctx.timestamp.getTime() < context.timestamp.getTime()
    );

    if (crossEntityContext) {
      if (crossEntityContext.sourceType === 'detail' && crossEntityContext.entityId) {
        return this.getDetailRoute(crossEntityContext.entityType!, crossEntityContext.entityId);
      } else if (crossEntityContext.sourceType === 'list') {
        return this.getListRoute(crossEntityContext.entityType);
      }
    }

    // PRIORITY 4: Handle source-based returns
    switch (context.sourceType) {
      case 'list':
        return this.getListRoute(context.entityType);

      case 'detail':
        // For detail views, return to the list view of the same entity type
        return this.getListRoute(context.entityType);

      case 'kanban':
        return '/maintenance/task-management-kanban';

      case 'dashboard':
        return '/';
    }

    // PRIORITY 5: Fallback to entity-specific list routes
    if (context.entityType) {
      return this.getListRoute(context.entityType);
    }

    return options.fallbackRoute || '/';
  }

  /**
   * Navigate back to the appropriate context
   */
  navigateBack(options: ReturnNavigationOptions = {}): Promise<boolean> {
    const returnRoute = this.getReturnRoute(options);

    const navigationExtras: { queryParams?: Record<string, unknown> } = {};

    if (options.preserveQueryParams && this._currentContext$.value?.sourceQueryParams) {
      navigationExtras.queryParams = {
        ...this._currentContext$.value.sourceQueryParams,
        ...options.additionalParams
      };
    } else if (options.additionalParams) {
      navigationExtras.queryParams = options.additionalParams;
    }

    this._isNavigatingBack = true;
    return this.router.navigateByUrl(returnRoute).catch(err => {
      this._isNavigatingBack = false;
      throw err;
    });
  }

  /**
   * Navigate to create/add form with proper context
   */
  navigateToCreate(entityType: string, contextParams: Record<string, unknown> = {}): Promise<boolean> {
    const currentContext = this._currentContext$.value;

    const queryParams: Record<string, unknown> = {
      returnContext: JSON.stringify({
        sourceRoute: this.router.url,
        sourceType: currentContext?.sourceType,
        parentEntityType: currentContext?.entityType,
        parentEntityId: currentContext?.entityId,
        parentTabOrSection: currentContext?.parentTabOrSection
      }),
      ...contextParams
    };

    const createRoute = this.getCreateRoute(entityType);
    return this.router.navigate([createRoute], { queryParams });
  }

  /**
   * Get list route for entity type
   */
  private getListRoute(entityType?: string): string {
    switch (entityType) {
      case 'property': return '/properties/list';
      case 'repair': return '/repairs/list';
      case 'maintenance': return '/maintenance/list';
      case 'contract': return '/contracts/list';
      case 'tenant': return '/tenants/list';
      case 'loan': return '/loans/list';
      case 'building': return '/properties/list'; // Buildings are accessed from properties
      case 'service-provider': return '/service-providers';
      case 'investment-calculator': return '/property-evaluation/investment-calculator/list';
      case 'help': return '/help/user-guide';
      case 'account-settings': return '/apps/user-profile';
      case 'admin': return '/admin';
      case 'communication': return '/apps/inbox';
      default: return '/';
    }
  }

  /**
   * Get detail route for entity
   */
  private getDetailRoute(entityType: string, entityId: string): string {
    switch (entityType) {
      case 'property': return `/properties/${entityId}`;
      case 'repair': return `/repairs/detail/${entityId}`;
      case 'maintenance': return `/maintenance/detail/${entityId}`;
      case 'contract': return `/contracts/detail/${entityId}`;
      case 'tenant': return `/tenants/${entityId}`;
      case 'loan': return `/loans/detail/${entityId}`;
      case 'building': return `/building/${entityId}`;
      case 'service-provider': return `/service-providers/${entityId}`;
      case 'investment-calculator': return `/property-evaluation/investment-calculator/${entityId}`;
      case 'help': return `/help/${entityId}`;
      case 'account-settings': return `/apps/user-profile?section=${entityId}`;
      case 'admin': return `/admin/${entityId}`;
      case 'communication': return `/apps/inbox?msg=${entityId}`;
      default: return '/';
    }
  }

  /**
   * Get create route for entity type
   */
  private getCreateRoute(entityType: string): string {
    switch (entityType) {
      case 'property': return '/properties/add';
      case 'repair': return '/repairs/create';
      case 'maintenance': return '/maintenance/create';
      case 'contract': return '/contracts/add';
      case 'tenant': return '/tenants/add';
      case 'loan': return '/loans/add';
      case 'building': return '/building/add';
      case 'service-provider': return '/service-providers/add';
      default: return '/';
    }
  }

  /**
   * Get current navigation context
   */
  getCurrentContext(): NavigationContext | null {
    return this._currentContext$.value;
  }

  /**
   * Get navigation history
   */
  getNavigationHistory(): NavigationContext[] {
    return [...this._navigationHistory];
  }

  /**
   * Clear navigation history
   */
  clearHistory(): void {
    this._navigationHistory = [];
  }

  /**
   * Check if we're in a specific context
   */
  isInContext(entityType: string, entityId?: string): boolean {
    const context = this._currentContext$.value;
    if (!context) return false;

    return context.entityType === entityType &&
      (!entityId || context.entityId === entityId);
  }

  /**
   * Check if we're in a parent context
   */
  isInParentContext(parentType: string, parentId?: string): boolean {
    const context = this._currentContext$.value;
    if (!context) return false;

    return context.parentEntityType === parentType &&
      (!parentId || context.parentEntityId === parentId);
  }
}
